import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Link } from 'react-router-dom'
import * as yup from 'yup'
import _ from '@lodash'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import FormHelperText from '@mui/material/FormHelperText'
import jwtService from '../../auth/services/jwtService'
import Dialog from '@mui/material/Dialog';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Terms from './Terms';

/**
 * Form Validation Schema
 */
const schema = yup.object().shape({
  name: yup.string().required('Você deve inserir o nome de exibição'),
  email: yup
    .string()
    .email('Você deve inserir um e-mail válido')
    .required('Você deve inserir um e-mail'),
  password: yup
    .string()
    .required('Por favor, insira sua senha.')
    .min(8, 'A senha é muito curta - deve ter no mínimo 8 caracteres.'),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref('password'), null], 'As senhas devem corresponder'),
  acceptTermsConditions: yup
    .boolean()
    .oneOf([true], 'Os Termos de Serviço e Política de Privacidade, e com os Termos de participação na Pesquisa de Mestrado devem ser aceitos.')
})

const defaultValues = {
  name: '',
  email: '',
  password: '',
  passwordConfirm: '',
  acceptTermsConditions: false
}

function SignUpPage() {
  const { control, formState, handleSubmit, reset } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema)
  })

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const { isValid, dirtyFields, errors, setError } = formState

  function onSubmit({ name, password, email }) {
    jwtService
      .createUser({
        name,
        password,
        email
      })
      .then(user => {
        // No need to do anything, registered user data will be set at app/auth/AuthContext
      })
      .catch(_errors => {
        _errors.forEach(error => {
          setError(error.type, {
            type: 'manual',
            message: error.message
          })
        })
      })
  }

  return (
    <div className='flex flex-col sm:flex-row items-center md:items-start sm:justify-center md:justify-start flex-1 min-w-0'>
      <Paper className='h-full sm:h-auto md:flex md:items-center md:justify-end w-full sm:w-auto md:h-full md:w-1/2 py-8 px-16 sm:p-48 md:p-64 sm:rounded-2xl md:rounded-none sm:shadow md:shadow-none ltr:border-r-1 rtl:border-l-1'>
        <div className='w-full max-w-500 sm:w-500 mx-auto sm:mx-0'>
          <img className='w-48' src='assets/images/logo/2201570.png' alt='logo' />

          <Typography className='mt-32 text-4xl font-extrabold tracking-tight leading-tight'>
            Cadastro
          </Typography>
          <div className='flex items-baseline mt-2 font-medium'>
            <Typography>Já tem uma conta?</Typography>
            <Link className='ml-4' to='/sign-in'>
              Login
            </Link>
          </div>

          <form
            name='registerForm'
            noValidate
            className='flex flex-col justify-center w-full mt-32'
            onSubmit={handleSubmit(onSubmit)}
          >
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className='mb-24'
                  label='Nome'
                  autoFocus
                  type='name'
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                  variant='outlined'
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className='mb-24'
                  label='Email'
                  type='email'
                  error={!!errors.email}
                  helperText={errors?.email?.message}
                  variant='outlined'
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='password'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className='mb-24'
                  label='Senha'
                  type='password'
                  error={!!errors.password}
                  helperText={errors?.password?.message}
                  variant='outlined'
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='passwordConfirm'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className='mb-24'
                  label='Senha (Confirmação)'
                  type='password'
                  error={!!errors.passwordConfirm}
                  helperText={errors?.passwordConfirm?.message}
                  variant='outlined'
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='acceptTermsConditions'
              control={control}
              render={({ field }) => (
                <FormControl
                  className='items-center'
                  error={!!errors.acceptTermsConditions}
                >
                  <FormControlLabel
                    label='Eu concordo com os Termos de Serviço e Política de Privacidade, e com os Termos de participação na Pesquisa de Mestrado.'
                    control={<Checkbox size='small' {...field} />}
                  />
                  <FormHelperText>
                    {errors?.acceptTermsConditions?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />

            <Button variant="outlined" onClick={handleClickOpen}>
              Termos
            </Button>
            <Dialog
              fullScreen
              open={open}
              onClose={handleClose}
            >
              <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={handleClose}
                    aria-label="close"
                  >
                    <CloseIcon />
                  </IconButton>
                  <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                    Termos
                  </Typography>
                </Toolbar>
              </AppBar>
              <List>
                <ListItem>
                  <Terms />
                </ListItem>
              </List>
            </Dialog>

            <Button
              variant='contained'
              color='secondary'
              className='w-full mt-24'
              aria-label='Register'
              disabled={_.isEmpty(dirtyFields) || !isValid}
              type='submit'
              size='large'
            >
              Cria a tua conta gratuita
            </Button>
          </form>
        </div>
      </Paper>

      <Box
        className='relative hidden md:flex flex-auto items-center justify-center h-full p-64 lg:px-112 overflow-hidden'
        sx={{ backgroundColor: 'primary.main' }}
      >
        <svg
          className='absolute inset-0 pointer-events-none'
          viewBox='0 0 960 540'
          width='100%'
          height='100%'
          preserveAspectRatio='xMidYMax slice'
          xmlns='http://www.w3.org/2000/svg'
        >
          <Box
            component='g'
            sx={{ color: 'primary.light' }}
            className='opacity-20'
            fill='none'
            stroke='currentColor'
            strokeWidth='100'
          >
            <circle r='234' cx='196' cy='23' />
            <circle r='234' cx='790' cy='491' />
          </Box>
        </svg>
        <Box
          component='svg'
          className='absolute -top-64 -right-64 opacity-20'
          sx={{ color: 'primary.light' }}
          viewBox='0 0 220 192'
          width='220px'
          height='192px'
          fill='none'
        >
          <defs>
            <pattern
              id='837c3e70-6c3a-44e6-8854-cc48c737b659'
              x='0'
              y='0'
              width='20'
              height='20'
              patternUnits='userSpaceOnUse'
            >
              <rect x='0' y='0' width='4' height='4' fill='currentColor' />
            </pattern>
          </defs>
          <rect
            width='220'
            height='192'
            fill='url(#837c3e70-6c3a-44e6-8854-cc48c737b659)'
          />
        </Box>

        <div className='z-10 relative w-full max-w-2xl'>
          <div className='text-3xl font-bold leading-none text-gray-100'>
            <div>Utilize o poder da Taxonomia de Bloom Revisada para criar descritores educacionais impactantes.</div>
          </div>
          <div className='mt-24 text-lg tracking-tight leading-6 text-gray-400'>
            Conheça a ferramenta ideal para desenvolver descritores de formação e avaliação alinhados com os níveis cognitivos da Taxonomia de Bloom Revisada.
          </div>
        </div>
      </Box>
    </div>
  )
}

export default SignUpPage
